import axios, { getData } from "../../../../axios";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { customerDeliveryLocationT, CustomerDeliveryLocation } from "./types";
import {
  CustomerDeliveryLocationUpdate,
  customerDeliveryLocationCreateT,
} from "dora-contracts";
import * as tPromise from "io-ts-promise";
import * as t from "io-ts";
import { createErrorReportingAsyncThunk } from "../../../helpers";

const prefix = "economics/data/customer-delivery-locations";
const customerDeliveryLocationsT = t.array(customerDeliveryLocationT);

type State = {
  entities: Record<string, CustomerDeliveryLocation>;
  idsByClientId: Record<string, string[]>;
  /**
   * The customer currently "in focus" (selected in the shipment dialog or open
   * on the customer page). Live coordinate-resolution updates are only applied
   * while their client is in focus.
   */
  focusedClientId: string | null;
  /** True while a delivery-locations fetch is in flight. */
  loading: boolean;
  /** The client whose delivery locations are currently loaded. */
  loadedClientId: string | null;
};

const initialState: State = {
  entities: {},
  idsByClientId: {},
  focusedClientId: null,
  loading: false,
  loadedClientId: null,
};

export const loadCustomerDeliveryLocations = createErrorReportingAsyncThunk(
  `${prefix}/load-customer-delivery-locations`,
  async (clientId: string): Promise<CustomerDeliveryLocation[]> => {
    return axios
      .get(`/api/e-conomic/customers/${clientId}/delivery-locations`)
      .then(getData)
      .then(tPromise.decode(customerDeliveryLocationsT));
  }
);

export const updateCustomerDeliveryLocation = createErrorReportingAsyncThunk(
  `${prefix}/update-customer-delivery-location`,
  async (input: {
    clientId: string;
    locationId: string;
    changes: CustomerDeliveryLocationUpdate;
  }): Promise<CustomerDeliveryLocation> => {
    return axios
      .put(
        `/api/e-conomic/customers/${input.clientId}/delivery-locations/${input.locationId}`,
        input.changes
      )
      .then(getData)
      .then(tPromise.decode(customerDeliveryLocationT));
  }
);

export const createCustomerDeliveryLocation = createErrorReportingAsyncThunk(
  `${prefix}/create-customer-delivery-location`,
  async (input: {
    clientId: string;
    name: string;
    address: string | null;
    postalCode: string | null;
    city: string | null;
    country: string | null;
    resolvedPlace?: CustomerDeliveryLocation["resolvedPlace"];
  }): Promise<CustomerDeliveryLocation> => {
    const { clientId, resolvedPlace, ...rest } = input;
    return axios
      .post(
        `/api/e-conomic/customers/${clientId}/delivery-locations`,
        customerDeliveryLocationCreateT.encode({
          ...rest,
          ...(resolvedPlace && { resolvedPlace }),
        })
      )
      .then(getData)
      .then(tPromise.decode(customerDeliveryLocationT));
  }
);

export const deleteCustomerDeliveryLocation = createErrorReportingAsyncThunk(
  `${prefix}/delete-customer-delivery-location`,
  async (input: {
    clientId: string;
    locationId: string;
  }): Promise<{ clientId: string; locationId: string }> => {
    await axios.delete(
      `/api/e-conomic/customers/${input.clientId}/delivery-locations/${input.locationId}`
    );
    return input;
  }
);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {
    setFocusedClient(state, action: PayloadAction<string>) {
      state.focusedClientId = action.payload;
    },
    clearFocusedClient(state) {
      state.focusedClientId = null;
    },
    // Live upsert from the socket (coordinates resolved, or a manual update by
    // another user). Only applied if the customer is still in focus.
    deliveryLocationUpserted(
      state,
      action: PayloadAction<CustomerDeliveryLocation>
    ) {
      const location = action.payload;
      if (location.clientId !== state.focusedClientId) {
        return;
      }
      state.entities[location.id] = location;
      const ids = state.idsByClientId[location.clientId] ?? [];
      if (!ids.includes(location.id)) {
        state.idsByClientId[location.clientId] = [...ids, location.id];
      }
    },
    // Live removal from the socket (deleted by another user). Only applied if
    // the customer is still in focus.
    deliveryLocationDeleted(
      state,
      action: PayloadAction<{ clientId: string; id: string }>
    ) {
      const { clientId, id } = action.payload;
      if (clientId !== state.focusedClientId) {
        return;
      }
      delete state.entities[id];
      state.idsByClientId[clientId] = (
        state.idsByClientId[clientId] ?? []
      ).filter((existingId) => existingId !== id);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadCustomerDeliveryLocations.pending, (state) => {
        // Reset while fetching so stale data isn't shown for the new client.
        state.loading = true;
        state.loadedClientId = null;
        state.idsByClientId = {};
        state.entities = {};
      })
      .addCase(loadCustomerDeliveryLocations.fulfilled, (state, action) => {
        state.idsByClientId = {};
        state.entities = {};
        const clientId = action.meta.arg;
        const ids = action.payload.map((x) => x.id);
        state.idsByClientId[clientId] = ids;
        for (const location of action.payload) {
          state.entities[location.id] = location;
        }
        state.loading = false;
        state.loadedClientId = clientId;
      })
      .addCase(loadCustomerDeliveryLocations.rejected, (state) => {
        state.loading = false;
      })
      .addCase(
        updateCustomerDeliveryLocation.fulfilled,
        (state, action) => {
          const location = action.payload;
          state.entities[location.id] = location;
        }
      )
      .addCase(
        createCustomerDeliveryLocation.fulfilled,
        (state, action) => {
          const location = action.payload;
          state.entities[location.id] = location;
          const ids = state.idsByClientId[location.clientId] ?? [];
          if (!ids.includes(location.id)) {
            state.idsByClientId[location.clientId] = [...ids, location.id];
          }
        }
      )
      .addCase(
        deleteCustomerDeliveryLocation.fulfilled,
        (state, action) => {
          const { clientId, locationId } = action.payload;
          delete state.entities[locationId];
          state.idsByClientId[clientId] = (
            state.idsByClientId[clientId] ?? []
          ).filter((id) => id !== locationId);
        }
      );
  },
});

export const {
  setFocusedClient,
  clearFocusedClient,
  deliveryLocationUpserted,
  deliveryLocationDeleted,
} = slice.actions;

export default slice.reducer;
